import React, { useState, useRef, useEffect } from 'react'
import { SingleGoalUpdate } from 'Generated/graphql-types'
import { Button, Loading } from '@resiliatech/birdsong'
import { Menu, MenuItem } from '@mui/material'
import { useMutation } from '@apollo/client'
import { useAppState } from 'Context/AppContext/AppContext'
import DELETE_GOAL_UPDATE from 'Apollo/Mutations/DeleteGoalUpdate'
import { GOAL_UPDATES } from 'Apollo/Queries/GoalUpdates'
import SortIcon from '@mui/icons-material/Sort'
import FilterListIcon from '@mui/icons-material/FilterList'
import ConfirmActionDialogSong from '../../../../SongComponents/ConfirmActionDialogSong/ConfirmActionDialogSong'
import TitleWithTooltipSong from '../../../../SongComponents/TitleWithTooltipSong/TitleWithTooltipSong'
import { SnapshotDialog } from '../ProgressUpdate/SnapshotDialog/SnapshotDialog'
import {
  UpdatesTabContainer,
  HeaderAndButtonContainer,
  ButtonContainer,
  DialogAnchor,
  UpdatesGrid,
  Column,
  ControlsContainer,
  SortButton,
  FilterButton,
} from './UpdatesTab.styled'
import { Update } from './Update'
import {
  dispatchAddUpdateClicked,
  dispatchProgressUpdateDeleted,
} from '../analytics.utils'
import { EmptyState } from './EmptyState'
import { GOALS } from '../../../../../Apollo/Queries/Goals'

type Props = {
  updates: SingleGoalUpdate[]
  updatesLoading: boolean
  goalId: number
  simple?: boolean
  externalSnapshotText?: string | null
  onExternalActionProcessed?: () => void
}

type SortOption = 'newest' | 'oldest'
type FilterOption = 'all' | 'withMedia' | 'withoutMedia'

export const UpdatesTab = ({
  updates = [],
  updatesLoading,
  goalId,
  simple,
  externalSnapshotText,
  onExternalActionProcessed,
}: Props) => {
  const { userConfig, nonprofitOrgConfig } = useAppState()
  const { id: userId } = userConfig
  const { orgId: nonprofitOrgId, platformPlan } = nonprofitOrgConfig

  const anchorRef = useRef<HTMLDivElement | null>(null)
  const [isProgressUpdateOpen, setIsProgressUpdateOpen] = useState(false)
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false)
  const [activeUpdate, setActiveUpdate] = useState<SingleGoalUpdate>()

  // Sort and filter states
  const [sortBy, setSortBy] = useState<SortOption>('newest')
  const [filterBy, setFilterBy] = useState<FilterOption>('all')
  const [sortAnchorEl, setSortAnchorEl] = useState<null | HTMLElement>(null)
  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null)

  // Handle external snapshot text - auto-open dialog with pre-filled content
  useEffect(() => {
    if (externalSnapshotText && !isProgressUpdateOpen) {
      try {
        // Set the active update with the external text to pre-fill the dialog
        setActiveUpdate({
          id: undefined,
          description: externalSnapshotText,
          createdAt: new Date().toISOString(),
          goalId,
          lastUpdatedUser: null,
          goalTitle: '',
          lastUpdatedUserFirstname: '',
          lastUpdatedUserId: null,
          lastUpdatedUserLastname: '',
          updatedAt: new Date().toISOString(),
        } as unknown as SingleGoalUpdate)

        // Open the progress update dialog
        setIsProgressUpdateOpen(true)

        // Notify parent that external action was processed
        onExternalActionProcessed?.()
      } catch (error) {
        console.error('Error processing external snapshot text:', error)
        // Reset the external text to prevent infinite retry
        // Note: This would need to be communicated back to parent component
        // For now, just log the error
      }
    }
  }, [
    externalSnapshotText,
    isProgressUpdateOpen,
    goalId,
    onExternalActionProcessed,
  ])

  const handleSortClick = (event: React.MouseEvent<HTMLElement>) => {
    setSortAnchorEl(event.currentTarget)
  }

  const handleFilterClick = (event: React.MouseEvent<HTMLElement>) => {
    setFilterAnchorEl(event.currentTarget)
  }

  const handleSortClose = () => {
    setSortAnchorEl(null)
  }

  const handleFilterClose = () => {
    setFilterAnchorEl(null)
  }

  const handleSortSelect = (option: SortOption) => {
    setSortBy(option)
    handleSortClose()
  }

  const handleFilterSelect = (option: FilterOption) => {
    setFilterBy(option)
    handleFilterClose()
  }

  const handleAddUpdate = () => {
    setIsProgressUpdateOpen(true)
    dispatchAddUpdateClicked({
      goalId,
      userId,
      platformPlan,
    })
  }

  const handleConfirmDelete = (update: SingleGoalUpdate) => {
    setActiveUpdate(update)
    setIsConfirmingDelete(true)
    dispatchProgressUpdateDeleted({
      progressUpdateId: update.id,
      goalId: update.goalId,
      goalTitle: update.goalTitle,
      userId,
      platformPlan,
      description: update.description,
    })
  }

  const [deleteGoalUpdate] = useMutation(DELETE_GOAL_UPDATE, {
    refetchQueries: [
      {
        query: GOAL_UPDATES,
        variables: { goalId, nonprofitOrgId },
      },
      {
        query: GOALS,
        variables: {
          activeOnly: true,
          goalId: goalId.toString(),
          nonprofitOrgId,
        },
      },
    ],
  })

  const handleDeleteUpdate = (goalUpdateId: number) => {
    deleteGoalUpdate({
      variables: { goalUpdateId, nonprofitOrgId },
    })
    setIsConfirmingDelete(false)
    setActiveUpdate(undefined)
  }

  const handleEditUpdate = (update: SingleGoalUpdate) => {
    setActiveUpdate(update)
    setIsProgressUpdateOpen(true)
  }

  // Apply sorting and filtering
  const filteredUpdates = [...updates]

  // Apply filtering
  // TODO: Once we implement attachments we need to make this real
  // if (filterBy === 'withMedia') {
  //   filteredUpdates = filteredUpdates.filter((update) => update.id % 3 === 0) // Simulating updates with media
  // } else if (filterBy === 'withoutMedia') {
  //   filteredUpdates = filteredUpdates.filter((update) => update.id % 3 !== 0) // Simulating updates without media
  // }

  // Apply sorting
  filteredUpdates.sort((a, b) => {
    const dateA = new Date(a.reportedAt || a.reportedAt || 0).getTime()
    const dateB = new Date(b.reportedAt || b.reportedAt || 0).getTime()
    return sortBy === 'newest' ? dateB - dateA : dateA - dateB
  })

  const hasUpdates = !!filteredUpdates.length

  return (
    <UpdatesTabContainer>
      <HeaderAndButtonContainer>
        <ControlsContainer>
          <SortButton onClick={handleSortClick}>
            <SortIcon fontSize='small' />
            Sort
          </SortButton>
          <Menu
            anchorEl={sortAnchorEl}
            open={Boolean(sortAnchorEl)}
            onClose={handleSortClose}
          >
            <MenuItem
              selected={sortBy === 'newest'}
              onClick={() => handleSortSelect('newest')}
            >
              Newest first
            </MenuItem>
            <MenuItem
              selected={sortBy === 'oldest'}
              onClick={() => handleSortSelect('oldest')}
            >
              Oldest first
            </MenuItem>
          </Menu>

          <FilterButton onClick={handleFilterClick}>
            <FilterListIcon fontSize='small' />
            Filters
          </FilterButton>
          <Menu
            anchorEl={filterAnchorEl}
            open={Boolean(filterAnchorEl)}
            onClose={handleFilterClose}
          >
            <MenuItem
              selected={filterBy === 'all'}
              onClick={() => handleFilterSelect('all')}
            >
              All snapshots
            </MenuItem>
            <MenuItem
              selected={filterBy === 'withMedia'}
              onClick={() => handleFilterSelect('withMedia')}
            >
              With media
            </MenuItem>
            <MenuItem
              selected={filterBy === 'withoutMedia'}
              onClick={() => handleFilterSelect('withoutMedia')}
            >
              Text only
            </MenuItem>
          </Menu>
        </ControlsContainer>

        <ButtonContainer>
          <Button
            variant='table'
            label='Add Snapshot'
            onClick={handleAddUpdate}
          />
        </ButtonContainer>
      </HeaderAndButtonContainer>

      {updatesLoading && <Loading />}

      {hasUpdates ? (
        <UpdatesGrid>
          {[0, 1, 2].map((columnIndex) => (
            <Column key={columnIndex}>
              {filteredUpdates
                .filter((_, index) => index % 3 === columnIndex)
                .map((update) => (
                  <Update
                    key={update.id}
                    update={update}
                    onDeleteUpdate={() => handleConfirmDelete(update)}
                    onEditUpdate={() => handleEditUpdate(update)}
                  />
                ))}
            </Column>
          ))}
        </UpdatesGrid>
      ) : (
        <EmptyState onAddSnapshot={handleAddUpdate} />
      )}

      <DialogAnchor ref={anchorRef}>
        <SnapshotDialog
          goalId={goalId}
          goalUpdateId={activeUpdate?.id}
          goalUpdateDescription={activeUpdate?.description}
          open={isProgressUpdateOpen}
          onUpdateComplete={() => setActiveUpdate(undefined)}
          onClose={() => {
            setIsProgressUpdateOpen(false)
          }}
        />
      </DialogAnchor>

      {isConfirmingDelete && activeUpdate && (
        <ConfirmActionDialogSong
          title='Delete Update'
          open={isConfirmingDelete}
          submitButtonText='Delete'
          onCancelHandler={() => setIsConfirmingDelete(false)}
          onSubmitHandler={() => handleDeleteUpdate(activeUpdate.id)}
        >
          <TitleWithTooltipSong title='Are you sure you want to delete this update?' />
        </ConfirmActionDialogSong>
      )}
    </UpdatesTabContainer>
  )
}
